import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Home, Zap, DollarSign, ArrowRight, ExternalLink, FileSignature } from "lucide-react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { useNoindex } from "@/hooks/use-seo";
import { marketingTitle } from "@/data/marketing-seo";
import { partnerPortalApplyUrl, partnerPortalUrl } from "@/lib/partner-portal-url";
import { PartnerFooter } from "@/components/partner-footer";

const APPLY_URL = partnerPortalApplyUrl("wholesaler");

const BENEFITS = [
  { icon: Home, title: "Deals auto-listed to PadPulse buyers", desc: "Your deals are automatically added to our internal certified-by page, in front of active co-living buyers." },
  { icon: Zap, title: "Auto buy-box matching", desc: "When a deal fits a host's buy box, PadPulse automatically notifies them — no manual outreach needed." },
  { icon: DollarSign, title: "Pay only on closed deals", desc: "No upfront cost. You pay a referral fee only when an assignment actually closes." },
  { icon: CheckCircle, title: "Airtable integration — coming Q3", desc: "Sync deal and referral data directly to your Airtable workspace. Rolling out Q3." },
];

export default function PartnerWholesalersPage() {
  usePageTitle(marketingTitle("Wholesaler Partners"), true);
  // The partner program is frozen (build F8): pages stay, but are noindexed and unlinked.
  useNoindex();

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">P</span>
            </div>
            <span className="font-bold text-slate-900">PadPulse</span>
          </a>
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-600">
            <Link href="/partners/lenders">Lenders</Link>
            <Link href="/partners/brokers">Brokers</Link>
            <Link href="/partners/realtors">Realtors</Link>
            <a href={partnerPortalUrl()} className="text-blue-600 font-medium flex items-center gap-1" target="_blank" rel="noopener noreferrer">
              Partner portal <ExternalLink className="h-3 w-3" />
            </a>
          </nav>
        </div>
      </header>

      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-violet-50 text-violet-700 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
            Wholesaler Partners
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Move deals faster with PadPulse buyers
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-8">
            Get your deals in front of PadPulse buyers automatically. Every deal that fits a host's buy box notifies them instantly — you pay a referral fee only when an assignment closes.
          </p>
          <Button size="lg" onClick={() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" })} data-testid="button-get-started">
            Apply to partner <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <section className="py-16 bg-slate-50 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-10">Why wholesalers choose PadPulse</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {BENEFITS.map((b) => (
              <Card key={b.title}>
                <CardContent className="p-6 flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                    <b.icon className="h-5 w-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">{b.title}</p>
                    <p className="text-sm text-slate-500">{b.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="py-20 px-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardContent className="p-8 text-center">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center mx-auto mb-5">
                <FileSignature className="h-6 w-6 text-violet-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Partner with PadPulse</h2>
              <p className="text-slate-500 mb-6">
                Apply and review and sign the PadPulse wholesaler partner agreement in one step. As soon as you've signed, our partnerships team reviews your application — usually within 2 business days.
              </p>
              <a href={APPLY_URL}>
                <Button size="lg" data-testid="button-apply">
                  Apply &amp; sign agreement <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <p className="text-xs text-slate-400 mt-4">You'll read and electronically sign the partner agreement as part of your application, so there's nothing to chase down later.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <PartnerFooter />
    </div>
  );
}
